import type { ApiError, ApiRequestOptions } from "./contract";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly payload: ApiError | null;

  constructor(status: number, payload: ApiError | null) {
    super(payload?.detail || payload?.message || "Something went wrong. Please try again.");
    this.name = "ApiRequestError";
    this.status = status;
    this.payload = payload;
  }
}

const csrfMethods = new Set(["POST", "PUT", "PATCH", "DELETE"]);
let csrfToken: string | null = null;
const csrfStorageKey = "epl_ff_csrf";

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
  if (token) sessionStorage.setItem(csrfStorageKey, token);
  else sessionStorage.removeItem(csrfStorageKey);
}

export async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const storedToken = sessionStorage.getItem(csrfStorageKey);
  if (storedToken) {
    setCsrfToken(storedToken);
    return storedToken;
  }
  const response = await fetch("/api/auth/csrf", { credentials: "same-origin" });
  if (!response.ok) throw new ApiRequestError(response.status, await readJson<ApiError>(response));
  const data = await readJson<{ csrf_token: string }>(response);
  if (!data?.csrf_token) throw new ApiRequestError(500, { detail: "CSRF token was not provided." });
  setCsrfToken(data.csrf_token);
  return data.csrf_token;
}

async function readJson<T>(response: Response): Promise<T | null> {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return (await response.json()) as T;
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = (options.method || "GET").toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.csrf && csrfMethods.has(method)) headers.set("X-CSRF-Token", await getCsrfToken());
  const response = await fetch(path, {
    ...options,
    method,
    headers,
    credentials: "same-origin",
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const data = await readJson<T & ApiError>(response);
  if (!response.ok) throw new ApiRequestError(response.status, data);
  return data as T;
}

export function clearApiSession(): void {
  setCsrfToken(null);
}
